/**
 * Legacy staff customer / user endpoints. Stage 1 drilldown reuses these
 * verbatim — no new BI endpoints needed for v1.
 *
 *   GET /api/users/                            paginated user list (DRF list view)
 *   GET /api/users/{id}/                       react_api UserDetailSerializer (verification, last_login, last_order_date)
 *   GET /staff/api/user/{id}/                  staff UserSerializer w/ summary {total_orders, total_sum} + last_latitude/longitude/address
 *   GET /api/users/{id}/cart/items/            current cart contents (when present)
 *
 * The two user endpoints return slightly different shapes; the BI customer
 * detail page uses both via {@link useCustomerSummary} to compose a single
 * KPI strip. Where the staff app has multiple list endpoints we use the most
 * generic one and rely on `?search=`, `?role=`, etc. to slice.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { get } from '../client';

// ---------------------------------------------------------------------------
// Customer list
// ---------------------------------------------------------------------------

export interface CustomerRow {
  id: number;
  name: string | null;
  username: string | null;
  /** react_api `UserSerializer` exposes phone via `phone_number`. */
  phone_number?: string | null;
  /** Mirrored from `phone_number` for legacy callers. */
  contact?: string | null;
  email?: string | null;
  is_verified: boolean;
  is_active?: boolean;
  date_joined: string | null;
  last_login?: string | null;
  last_active?: string | null;
  role?: string | null;
  /** Geo (last known) — only present from `/staff/api/user/{id}/`. */
  last_latitude?: number | string | null;
  last_longitude?: number | string | null;
  last_address?: string | null;
  /** Aggregate roll-ups, populated by `useCustomerSummary`. */
  orders_count?: number | null;
  total_orders?: number | null;
  total_spent?: number | null;
  avg_order_value?: number | null;
  last_order_at?: string | null;
  [k: string]: unknown;
}

export interface CustomerListResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: CustomerRow[];
  // Some installs use the custom paginator → `total_items` instead of `count`.
  total_items?: number;
  has_next_page?: boolean;
  page?: number;
}

export interface CustomerListParams {
  search?: string;
  role?: string;
  isVerified?: boolean | null;
  page?: number;
  pageSize?: number;
}

function customerParams(p: CustomerListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: p.page ?? 1,
    page_size: p.pageSize ?? 25,
  };
  if (p.search) out.search = p.search;
  if (p.role) out.role = p.role;
  if (p.isVerified != null) out.is_verified = p.isVerified ? 'true' : 'false';
  return out;
}

export function useCustomerList(p: CustomerListParams) {
  return useQuery({
    queryKey: [
      'legacy', 'customers-list',
      p.search ?? '', p.role ?? '', p.isVerified ?? null,
      p.page ?? 1, p.pageSize ?? 25,
    ],
    queryFn: () =>
      get<CustomerListResponse>('/api/users/', { params: customerParams(p) }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Customer detail
// ---------------------------------------------------------------------------

/**
 * Shape returned by `/api/users/{id}/` (react_api `UserDetailSerializer`).
 * Notable BI-relevant fields: `phone_number`, `last_login`, `last_active`,
 * `last_order_date`, `is_verified`. NO geo/lifetime totals here.
 */
export interface CustomerDetail {
  id: number;
  name: string | null;
  username: string | null;
  phone_number?: string | null;
  email?: string | null;
  role?: string | null;
  is_verified: boolean;
  is_active?: boolean;
  date_joined: string | null;
  last_login?: string | null;
  last_active?: string | null;
  /** `UserDetailSerializer.get_last_order_date` — newest completed order. */
  last_order_date?: string | null;
  has_cart_products?: boolean;
  is_active_in_range?: boolean | null;
  is_converted_in_range?: boolean | null;
  [k: string]: unknown;
}

/**
 * Shape returned by `/staff/api/user/{id}/` (staff `UserSerializer`).
 * This one carries last-known geo + a small `summary` block with completed
 * order count and total spend. We use both endpoints in the BI customer
 * 360 view because neither covers everything by itself.
 */
export interface CustomerStaffSummary {
  id: number;
  name: string | null;
  phone_number?: string | null;
  date_joined?: string | null;
  last_active?: string | null;
  last_latitude?: number | string | null;
  last_longitude?: number | string | null;
  last_address?: string | null;
  summary?: {
    total_orders?: number | null;
    total_sum?: number | null;
  } | null;
}

export function useCustomerDetail(userId: number | null) {
  return useQuery({
    queryKey: ['legacy', 'customer-detail', userId],
    queryFn: () => get<CustomerDetail>(`/api/users/${userId}/`),
    enabled: userId != null && userId > 0,
    staleTime: 60_000,
  });
}

export function useCustomerStaffSummary(userId: number | null) {
  return useQuery({
    queryKey: ['legacy', 'customer-staff-summary', userId],
    queryFn: () => get<CustomerStaffSummary>(`/staff/api/user/${userId}/`),
    enabled: userId != null && userId > 0,
    staleTime: 60_000,
    retry: 0,
  });
}

export interface CartItemSnapshot {
  id: number;
  product_name?: string;
  name?: string;
  quantity: number;
  price?: number | string;
  amount?: number | string;
  marked_price?: number | string;
  added_at?: string;
  [k: string]: unknown;
}

export interface CartItemsResponse {
  results?: CartItemSnapshot[];
  items?: CartItemSnapshot[];
  [k: string]: unknown;
}

/**
 * Best-effort cart fetch — the endpoint returns 404 when the user has no
 * active cart, so we silence retries and let the caller treat "no cart"
 * as the empty state.
 */
export function useCustomerCart(userId: number | null) {
  return useQuery({
    queryKey: ['legacy', 'customer-cart', userId],
    queryFn: () => get<CartItemsResponse>(`/api/users/${userId}/cart/items/`),
    enabled: userId != null && userId > 0,
    staleTime: 60_000,
    retry: 0,
  });
}
